// Shared Business / Address / Bank field definitions + lightweight client-side
// format checks for the Add Partner dialog (index.tsx) and the partner detail
// edit sections (PartnerDetail.tsx) — both forms collect the exact same
// fields the backend's businessSchema/addressSchema/bankSchema
// (onboarding.validators.js) validate, and the regexes below are copied
// directly from the backend's own Utils/inValidators.js so client-side and
// server-side rules never drift apart.

export const BUSINESS_TYPE_OPTIONS: { value: 'LIMITED_COMPANY' | 'SOLE_TRADER'; label: string }[] = [
  { value: 'LIMITED_COMPANY', label: 'Limited Company' },
  { value: 'SOLE_TRADER', label: 'Sole Trader' },
];

export const PARTNER_TYPE_LABELS: Record<string, string> = {
  ESTATE_AGENT: 'Estate Agent',
  PROPERTY_DEVELOPER: 'Property Developer',
  ENERGY_ASSESSOR: 'Energy Assessor',
};

export const PARTNER_TYPE_OPTIONS = [
  'INSTALLER',
  'PLUMBER',
  'ELECTRICIAN',
  'ARCHITECT',
  'ESTATE_AGENT',
  'PROPERTY_DEVELOPER',
  'ENERGY_ASSESSOR',
  'MERCHANT',
];

export const partnerTypeLabel = (value?: string | null): string => {
  if (!value) return 'N/A';
  if (PARTNER_TYPE_LABELS[value]) return PARTNER_TYPE_LABELS[value];
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export interface BusinessFormState {
  businessType: 'LIMITED_COMPANY' | 'SOLE_TRADER' | '';
  legalName: string;
  tradingName: string;
  cin: string;
  pan: string;
  gstin: string;
}

export const emptyBusinessForm: BusinessFormState = {
  businessType: '',
  legalName: '',
  tradingName: '',
  cin: '',
  pan: '',
  gstin: '',
};

export interface AddressFormState {
  pincode: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
}

export const emptyAddressForm: AddressFormState = {
  pincode: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
};

export interface BankFormState {
  accountHolderName: string;
  ifscCode: string;
  accountNumber: string;
}

export const emptyBankForm: BankFormState = {
  accountHolderName: '',
  ifscCode: '',
  accountNumber: '',
};

// Mirrors acdoctorserverapp/src/Utils/inValidators.js exactly.
const IN_PHONE_NUMBER_REGEX = /^[6-9]\d{9}$/;
export const isValidInPhoneNumber = (value: string) => IN_PHONE_NUMBER_REGEX.test(value.trim());

const IN_PINCODE_REGEX = /^[1-9]\d{5}$/;
export const isValidPincode = (value: string) => IN_PINCODE_REGEX.test(value.trim());

const CIN_REGEX = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;
export const isValidCin = (value: string) => CIN_REGEX.test(value.replace(/\s+/g, '').toUpperCase());

const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
export const isValidPan = (value: string) => PAN_REGEX.test(value.replace(/\s+/g, '').toUpperCase());

const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
export const isValidGstin = (value: string) => GSTIN_REGEX.test(value.replace(/\s+/g, '').toUpperCase());

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const isValidIfsc = (value: string) => IFSC_REGEX.test(value.replace(/\s+/g, '').toUpperCase());

export const isValidAccountNumber = (value: string) => /^\d{9,18}$/.test(value.replace(/\s+/g, '').trim());

export const validateBusinessForm = (form: BusinessFormState): string | null => {
  if (!form.businessType) return 'Business type is required';
  if (!form.legalName.trim()) return 'Legal name is required';
  if (form.businessType === 'LIMITED_COMPANY') {
    if (!form.cin.trim()) return 'CIN is required for a limited company';
    if (!isValidCin(form.cin)) return 'Invalid CIN format';
  }
  if (form.businessType === 'SOLE_TRADER') {
    if (!form.pan.trim()) return 'PAN is required for a sole trader';
    if (!isValidPan(form.pan)) return 'Invalid PAN format (expected ABCDE1234F)';
  }
  if (form.gstin.trim() && !isValidGstin(form.gstin)) return 'Invalid GSTIN format';
  return null;
};

export const validateAddressForm = (form: AddressFormState): string | null => {
  if (!form.pincode.trim() || !isValidPincode(form.pincode)) return 'Enter a valid 6-digit PIN code';
  if (!form.line1.trim()) return 'Address line 1 is required';
  if (!form.city.trim()) return 'City is required';
  if (!form.state.trim()) return 'State is required';
  return null;
};

export const validateBankForm = (form: BankFormState): string | null => {
  if (!form.accountHolderName.trim()) return 'Account holder name is required';
  if (!isValidIfsc(form.ifscCode)) return 'Enter a valid IFSC code (e.g. HDFC0001234)';
  if (!isValidAccountNumber(form.accountNumber)) return 'Account number must be 9 to 18 digits';
  return null;
};

export const businessFormToPayload = (form: BusinessFormState) => ({
  businessType: form.businessType as 'LIMITED_COMPANY' | 'SOLE_TRADER',
  legalName: form.legalName.trim(),
  tradingName: form.tradingName.trim() || undefined,
  cin: form.businessType === 'LIMITED_COMPANY' ? form.cin.trim().toUpperCase() : undefined,
  pan: form.businessType === 'SOLE_TRADER' ? form.pan.trim().toUpperCase() : undefined,
  gstin: form.gstin.trim() ? form.gstin.trim().toUpperCase() : undefined,
});

export const addressFormToPayload = (form: AddressFormState) => ({
  pincode: form.pincode.trim(),
  line1: form.line1.trim(),
  line2: form.line2.trim() || undefined,
  city: form.city.trim(),
  state: form.state.trim(),
});

export const bankFormToPayload = (form: BankFormState) => ({
  accountHolderName: form.accountHolderName.trim(),
  ifscCode: form.ifscCode.trim().toUpperCase(),
  accountNumber: form.accountNumber.trim(),
});
